import { Filter } from "../Filters";
import {
	startOfDay,
	endOfDay,
	addDays,
	addMonths,
	addWeeks,
	startOfMonth,
	endOfMonth,
} from "date-fns";
import { getWeekStartDate, getWeekEndDate } from "../../../utils/dateUtils";
import { DYNAMIC_AND_STATIC_DATE_OPTIONS } from "../filterTypes";
import { v4 as uuidv4 } from "uuid";

export const DYNAMIC_AND_STATIC_DATE_OPERATORS = DYNAMIC_AND_STATIC_DATE_OPTIONS.map((option) => option.value);

export const DYNAMIC_DATE_VALUES = ["today", "yesterday", "before_yesterday", "tomorrow", "after_tomorrow", "this_week", "last_week", "previous_previous_week", "next_week", "next_next_week", "this_month", "last_month", "previous_previous_month", "next_month", "next_next_month", "this_and_last_month", "this_and_next_month"];

export const PICKED_DATE_PREFIX = "picked_";

export const isPickedDateValue = (value: string) => {
	if (typeof value !== "string" || !value.startsWith(PICKED_DATE_PREFIX)) return false;
	const parts = value.split("_");
	const hasThreeParts = parts.length === 3;
	const hasValidDirection = parts[2] === "last" || parts[2] === "next";
	const hasValidDays = !isNaN(parseInt(parts[1], 10));
	const isValidPickedValue = hasThreeParts && hasValidDirection && hasValidDays;
	return isValidPickedValue;
};

export const isDynamicDateValue = (value: string) => {
	const isStaticDynamicValue = DYNAMIC_DATE_VALUES.includes(value);
	const isPickedValue = isPickedDateValue(value);
	return isStaticDynamicValue || isPickedValue;
};

export const parsePickedDateValue = (value: string) => {
	const parts = value.split("_");
	const days = parseInt(parts[1], 10);
	const direction = parts[2] as "last" | "next";
	return { days, direction };
};

export const normalizeDynamicDateFilters = (filters: Array<Filter>): Array<Filter> => {
	const expandedFilters = filters.flatMap((filter) => normalizeDynamicDateOperators(filter));
	return expandedFilters;
};

const normalizeDynamicDateOperators = (filter: Filter): Array<Filter> => {
	if (DYNAMIC_AND_STATIC_DATE_OPERATORS.includes(filter.operator)) {
		const relationalValue = filter.value;
		let { dateValue, dateValueEnd } = transformDateRelativeValueToFixedValue(relationalValue, filter.operator);

		let normalizedOperator: string;
		let normalizedValue: string | null = null

		switch (filter.operator) {
			case "greater":
				normalizedOperator = "_gt";
				normalizedValue = dateValue ? endOfDay(dateValue).toISOString() : null;
				break;
			case "greater_or_equal":
				normalizedOperator = "_gte";
				normalizedValue = dateValue ? startOfDay(dateValue).toISOString() : null;
				break;
			case "less":
				normalizedOperator = "_lt";
				normalizedValue = dateValue ? startOfDay(dateValue).toISOString() : null;
				break;
			case "less_or_equal":
				normalizedOperator = "_lte";
				normalizedValue = dateValue ? endOfDay(dateValue).toISOString() : null;
				break;
		case "equal":
			normalizedOperator = "_eq";
			if (dateValue) {
				const groupId = `${filter.field.field}_${uuidv4()}`;
				const filterWithGte = { ...filter, operator: "_gte", value: startOfDay(dateValue).toISOString(), groupId };
				const filterWithLte = { ...filter, operator: "_lte", value: dateValueEnd ? endOfDay(dateValueEnd).toISOString() : endOfDay(dateValue).toISOString(), groupId };
				return [filterWithGte, filterWithLte];
			}
			break;
			default:
				return [filter];
		}
		
		if (dateValue) return [{ ...filter, operator: normalizedOperator, value: normalizedValue }];
		else return [{ ...filter, operator: normalizedOperator }];
	}

	return [filter];
};

const transformDateRelativeValueToFixedValue = (relativeValue: string, operator:string) => {
	const today = new Date();
	let dateValue: Date | null = null;
	let dateValueEnd: Date | null = null;

	const andOperators = ["equal"]
	const isSingleValueOperator = !andOperators.includes(operator)
	const usesStartBoundary = ["less", "greater_or_equal"].includes(operator)
	const usesEndBoundary = ["greater", "less_or_equal"].includes(operator)

	if (isPickedDateValue(relativeValue)) {
		const { days, direction } = parsePickedDateValue(relativeValue);
		if (direction === "last") {
			dateValue = startOfDay(addDays(today, -days));
			dateValueEnd = endOfDay(today);
		} else if (direction === "next") {
			dateValue = startOfDay(today);
			dateValueEnd = endOfDay(addDays(today, days));
		}
		return { dateValue, dateValueEnd };
	}

		switch (relativeValue) {
			case "today":
				if (isSingleValueOperator) {
					dateValue = endOfDay(today);
				} else {
					dateValue = startOfDay(today);
					dateValueEnd = endOfDay(today);
				}
				break;
			case "yesterday":
				if (isSingleValueOperator) {
					dateValue = endOfDay(addDays(today, -1));
				} else {
					dateValue = startOfDay(addDays(today, -1));
					dateValueEnd = endOfDay(addDays(today, -1));
				}
				break;
			case "before_yesterday":
				if (isSingleValueOperator) {
					dateValue = endOfDay(addDays(today, -2));
				} else {
					dateValue = startOfDay(addDays(today, -2));
					dateValueEnd = endOfDay(addDays(today, -2));
				}
				break;
			case "tomorrow":
				if (isSingleValueOperator) {
					dateValue = endOfDay(addDays(today, 1));
				} else {
					dateValue = startOfDay(addDays(today, 1));
					dateValueEnd = endOfDay(addDays(today, 1));
				}
				break;
			case "after_tomorrow":
				if (isSingleValueOperator) {
					dateValue = endOfDay(addDays(today, 2));
				} else {
					dateValue = startOfDay(addDays(today, 2));
					dateValueEnd = endOfDay(addDays(today, 2));
				}
				break;
	
		case "this_week":
			if (usesStartBoundary) {
				dateValue = getWeekStartDate(today);
			} else if (usesEndBoundary) {
				dateValue = getWeekEndDate(today);
			} else {
				dateValue = getWeekStartDate(today);
				dateValueEnd = getWeekEndDate(today);
			}
			break;
		case "last_week":
			if (usesStartBoundary) {
				dateValue = getWeekStartDate(addWeeks(today, -1));
			} else if (usesEndBoundary) {
				dateValue = getWeekEndDate(addWeeks(today, -1));
			} else {
				dateValue = getWeekStartDate(addWeeks(today, -1));
				dateValueEnd = getWeekEndDate(addWeeks(today, -1));
			}
			break;
		case "previous_previous_week":
			dateValue = getWeekStartDate(addWeeks(today, -2));
			dateValueEnd = getWeekEndDate(addWeeks(today, -2));
			break;
		case "next_week":
			if (usesStartBoundary) {
				dateValue = getWeekStartDate(addWeeks(today, 1));
			} else if (usesEndBoundary) {
				dateValue = getWeekEndDate(addWeeks(today, 1));
			} else {
				dateValue = getWeekStartDate(addWeeks(today, 1));
				dateValueEnd = getWeekEndDate(addWeeks(today, 1));
			}
			break;
		case "next_next_week":
			dateValue = getWeekStartDate(addWeeks(today, 2));
			dateValueEnd = getWeekEndDate(addWeeks(today, 2));
			break;
		case "this_month":
			if (usesStartBoundary) {
				dateValue = startOfMonth(today);
			} else if (usesEndBoundary) {
				dateValue = endOfMonth(today);
			} else {
				dateValue = startOfMonth(today);
				dateValueEnd = endOfMonth(today);
			}
			break;
		case "last_month":
			if (usesStartBoundary) {
				dateValue = startOfMonth(addMonths(today, -1));
			} else if (usesEndBoundary) {
				dateValue = endOfMonth(addMonths(today, -1));
			} else {
				dateValue = startOfMonth(addMonths(today, -1));
				dateValueEnd = endOfMonth(addMonths(today, -1));
			}
			break;
		case "previous_previous_month":
			dateValue = startOfMonth(addMonths(today, -2));
			dateValueEnd = endOfMonth(addMonths(today, -2));
			break;
		case "next_month":
			if (usesStartBoundary) {
				dateValue = startOfMonth(addMonths(today, 1));
			} else if (usesEndBoundary) {
				dateValue = endOfMonth(addMonths(today, 1));
			} else {
				dateValue = startOfMonth(addMonths(today, 1));
				dateValueEnd = endOfMonth(addMonths(today, 1));
			}
			break;
		case "next_next_month":
			dateValue = startOfMonth(addMonths(today, 2));
			dateValueEnd = endOfMonth(addMonths(today, 2));
			break;
		case "this_and_last_month":
			dateValue = startOfMonth(addMonths(today, -1));
			dateValueEnd = endOfMonth(today);
			break;
		case "this_and_next_month":
			dateValue = startOfMonth(today);
			dateValueEnd = endOfMonth(addMonths(today, 1));
			break;
		default:
			break;
	}

		return { dateValue, dateValueEnd };
}

export const transformRelativeDate = (matchingModalFilter: Filter, filter: Record<string, any>) => {
	const { dateValue, dateValueEnd } = transformDateRelativeValueToFixedValue(
		matchingModalFilter.value, matchingModalFilter.operator
	);
	if (dateValueEnd) {
		// means format is _and: [...]
		const firstOperator = Object.keys(filter._and[0][matchingModalFilter.field.field])[0];
		const secondOperator = Object.keys(filter._and[1][matchingModalFilter.field.field])[0];

		const completeFilter = {
			_and: [
				{ [matchingModalFilter.field.field]: { [firstOperator]: dateValue } },
				{ [matchingModalFilter.field.field]: { [secondOperator]: dateValueEnd } },
			],
		};
		return completeFilter
	} else {
		const operator = Object.keys(filter[matchingModalFilter.field.field])[0];
		const completeFilter = { [matchingModalFilter.field.field]: { [operator]: dateValue } };
		return completeFilter
	}
}